import type { EsalaryUserJSON } from '@hr/api/model'
import { TopMenuContainer } from '@hr/layout/TopMenu.styles.ts'
import { useCallback, useEffect, useState } from 'react'
import { Badge, Col, Container, Form, Nav, Navbar, NavDropdown } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router'
import { postLogout } from '../actions/authentication.js'
import { getNotificationsUnreadCount } from '../actions/notifications/notificationsgetunreadcount.js'
import { ChangePassword } from '../common/changepassword.jsx'
import {
	IconAccountCash,
	IconHelpCenterRound,
	IconHome,
	IconManagement,
	IconNotifications,
	IconOrganization,
	IconPerson,
	IconSettings,
	IconStatistics,
	MainSettings,
} from '../common/icons.jsx'
import { ImportCsvModal } from '../common/importcsvmodal.jsx'
import { ImportDimensionsModal } from '../common/importdimensionsmodal.jsx'
import { ImportEmployeesModal } from '../common/importemployeesmodal.jsx'
import LanguageSelector from '../common/languageselect.jsx'
import { MfaSetup } from '../common/mfasetup.tsx'
import { NetvisorModal } from '../common/netvisormodal.jsx'
import { ProcountorInvalidationModal } from '../common/procountorinvalidationmodal.jsx'
import { ProcountorModal } from '../common/procountormodal.jsx'
import { PsaImportExportModal } from '../common/psaimportexportmodal.jsx'
import { topPath } from '../common/settings.js'
import { SeveraExportModal } from '../common/severaexportmodal.jsx'
import { NOTIFICATIONS_ENABLED } from '../common/utils.jsx'
import { hrEsalary } from '../main.jsx'
import { NotifCanvas } from '../notifview/notifcanvas.jsx'
import { activeIntegrationsSelector, authSelector, unreadNotifCountSelector } from '../reducers/index.js'

export default function TopMenu({ currentUserPerson }) {
	const { t } = useTranslation()
	const navigate = useNavigate()

	const dispatch = useDispatch()
	const logout = useCallback(() => dispatch(postLogout()), [dispatch])

	const auth: EsalaryUserJSON = useSelector(authSelector)
	const isBasicLevel = auth.hrLevel !== 'premium'

	const unreadNotifCount = useSelector(unreadNotifCountSelector)
	const activeIntegrations = useSelector(activeIntegrationsSelector) || {}

	useEffect(() => {
		if (NOTIFICATIONS_ENABLED) {
			dispatch(getNotificationsUnreadCount())
		}
	}, [dispatch])

	const [showModal, setShowModal] = useState({})
	const [showNotif, setShowNotif] = useState(false)
	const handleShowNotif = () => setShowNotif(true)

	let mgmtPath: string

	if (auth.persons) {
		mgmtPath = 'persons'
	} else if (auth.knowledge) {
		mgmtPath = 'competences'
	} else if (auth.contracts) {
		mgmtPath = 'contracts'
	} else if (auth.discussions) {
		mgmtPath = 'discussions'
	} else if (auth.equipment) {
		mgmtPath = 'equipment'
	}

	const roleAdmin = auth.role === 'ADMIN' || auth.role === 'SYSTEM'
	const supervisor = auth.role === 'SUPERVISOR'
	const equipmentManager = auth.role === 'EQUIPMENT'
	//auth.personalProfile === auth.role === 'EMPLOYEE'

	const hideModal = (modal) => () => setShowModal({ [modal]: false }) // hide specific modal

	// https://github.com/react-bootstrap/react-router-bootstrap/issues/242#issuecomment-480330910
	// Fix two LinkContainers active at once: <Nav.Link active={false}>
	return (
		<>
			{NOTIFICATIONS_ENABLED && !equipmentManager && showNotif && <NotifCanvas show setShow={setShowNotif} />}

			{showModal.esalary && <ImportEmployeesModal hideModal={hideModal('esalary')} />}
			{showModal.importDimensions && <ImportDimensionsModal hideModal={hideModal('importDimensions')} />}
			{showModal.importCsv && <ImportCsvModal hideModal={hideModal('importCsv')} />}
			{showModal.psa && <PsaImportExportModal hideModal={hideModal('psa')} />}
			{showModal.severa && <SeveraExportModal hideModal={hideModal('severa')} />}
			{showModal.netvisor && <NetvisorModal hideModal={hideModal('netvisor')} />}
			{showModal.procountor && <ProcountorModal hideModal={hideModal('procountor')} />}
			{showModal.procountorInvalidation && <ProcountorInvalidationModal hideModal={hideModal('procountor')} />}
			{showModal.mfa && <MfaSetup hideModal={hideModal('mfa')} />}

			<TopMenuContainer
				expand="md"
				className="topmenu px-3"
				onToggle={(show) => {
					const sidebar = document.querySelector('aside')
					if (show) {
						sidebar.classList.remove('d-none')
					} else {
						sidebar.classList.add('d-none')
					}
				}}
			>
				<Container fluid>
					<Navbar.Toggle aria-controls="basic-navbar-nav" />
					<Navbar.Collapse id="basic-navbar-nav">
						<Nav>
							{!auth.personalProfile && (
								<Nav.Link as={Link} to="/" active={false}>
									<IconHome />
									<span>{t('Frontpage', 'Etusivu')}</span>
								</Nav.Link>
							)}

							{!!auth.employeeNumber && currentUserPerson && (
								<Nav.Link
									as={Link}
									to={`/personal/${topPath.personal || 'dashboard'}`}
									active={location.pathname.startsWith('/personal')}
								>
									<IconPerson />
									<span>{t('MyProfile', 'Oma profiili')}</span>
								</Nav.Link>
							)}
							{!auth.personalProfile &&
								(auth.persons || auth.knowledge || auth.contracts || auth.discussions || auth.equipment) && (
									<Nav.Link
										as={Link}
										to={`/mgmt/${topPath.mgmt || mgmtPath}`}
										active={location.pathname.startsWith('/mgmt')}
									>
										<IconManagement />
										<span>{t('Personnel', 'Henkilöstö')}</span>
									</Nav.Link>
								)}
							{!auth.personalProfile && !equipmentManager && auth.statistics && (
								<Nav.Link
									as={Link}
									to={`/stats/${topPath.stats || 'anniversaries'}`}
									active={location.pathname.startsWith('/stats')}
								>
									<IconStatistics />
									<span>{t('Statistics', 'Tilastot')}</span>
								</Nav.Link>
							)}
							{!auth.personalProfile && !equipmentManager && auth.organization && (
								<Nav.Link
									as={Link}
									to={`/organization/${topPath.organization || (auth.admin ? 'details' : 'documents')}`}
									active={location.pathname.startsWith('/organization')}
								>
									<IconOrganization />
									<span>{t('Organization', 'Organisaatio')}</span>
								</Nav.Link>
							)}
							{!auth.personalProfile &&
								roleAdmin &&
								auth.admin &&
								!supervisor &&
								!equipmentManager &&
								auth.organization && (
									<Nav.Link as={Link} to="/settings/users" active={location.pathname.startsWith('/settings')}>
										<MainSettings />
										<span>{t('Settings', 'Asetukset')}</span>
									</Nav.Link>
								)}
							{!auth.personalProfile && !equipmentManager && (auth.presalary || auth.paymentsApproval) && (
								<Nav.Link
									as={Link}
									to={`/epalkat/${topPath.epalkat || 'one-time-payment'}`}
									active={location.pathname.startsWith('/epalkat')}
								>
									<IconAccountCash />
									<span>{t('eSalary', 'ePalkat')}</span>
								</Nav.Link>
							)}
						</Nav>
						<Col>
							{location.hostname !== hrEsalary && (
								<Form.Control
									name="search"
									type="search"
									size="sm"
									placeholder={`🔍︎ ${t('SearchAnything', 'Etsi mitä tahansa')}…`}
									style={{ fontSize: '85%', background: 'var(--heeros-background)' }}
								/>
							)}
						</Col>
						{!auth.personalProfile && !isBasicLevel && !equipmentManager && (
							<Nav title={t('Notifications', 'Ilmoitukset')}>
								<Nav.Link active={showNotif} onClick={handleShowNotif}>
									{unreadNotifCount > 0 && <Badge pill>{unreadNotifCount}</Badge>}
									<IconNotifications />
								</Nav.Link>
							</Nav>
						)}

						<Nav title="Help Center">
							<Nav.Link
								active={false}
								href="https://helpcenter.heeros.com/hc/fi/categories/16281817247762-HR"
								target="_blank"
							>
								<IconHelpCenterRound />
							</Nav.Link>
						</Nav>

						<Col sm="auto">
							<div className="d-flex align-items-center settings">
								{/*<div className="vr me-1" />*/}
								<NavDropdown
									align="end"
									title={
										<i title={t('Settings', 'Asetukset')}>
											<IconPerson supervisor={supervisor} basic={isBasicLevel} />
										</i>
									}
								>
									<NavDropdown.Item
										onClick={(e) => e.stopPropagation()}
										style={{ cursor: 'default', color: 'grey' }}
										title={auth.employeeNumber ?? ''}
									>
										{supervisor ? `${t('Supervisor', 'Esihenkilö')} ` : ''}
										{auth.fullName}
									</NavDropdown.Item>
									<NavDropdown.Item
										title={`${auth.companyNumber} / ${auth.companyBusinessId}`}
										className="company-url"
										href={`//${auth.companyUrl}`}
										target="_blank"
									>
										{auth.companyName}
									</NavDropdown.Item>

									{!auth.personalProfile &&
										!equipmentManager &&
										auth.admin &&
										!supervisor && ( // auth.admin is controlled from ePalkat settings permissions
											<div className="helpcenter d-flex">
												<NavDropdown align="end" title={<IconSettings />}>
													<NavDropdown.Item disabled>eSalary-HR {import.meta.env.VITE_VERSION}</NavDropdown.Item>

													{!activeIntegrations.tokenProcountor && (
														<NavDropdown.Item onClick={() => setShowModal({ esalary: true })}>
															{t('ImportPersonsFromEsalary', 'Tuo henkilöt ePalkoista')}
														</NavDropdown.Item>
													)}

													{!activeIntegrations.tokenProcountor && (
														<NavDropdown.Item onClick={() => setShowModal({ importDimensions: true })}>
															{t('ImportDimensionsFromEsalary', 'Tuo dimensiot ePalkoista')}
														</NavDropdown.Item>
													)}

													<NavDropdown.Item onClick={() => setShowModal({ importCsv: true })}>
														{t('CsvFileImport', 'CSV tiedostotuonti')}
													</NavDropdown.Item>

													{activeIntegrations.tokenPsa && (
														<NavDropdown.Item onClick={() => setShowModal({ psa: true })}>
															{t('PsaImportExport', 'PSA tuonti/vienti')}
														</NavDropdown.Item>
													)}

													{activeIntegrations.tokenSevera && (
														<NavDropdown.Item onClick={() => setShowModal({ severa: true })}>
															{t('SeveraExport', 'Visma Severa vienti')}
														</NavDropdown.Item>
													)}

													{activeIntegrations.tokenNetvisor && (
														<NavDropdown.Item onClick={() => setShowModal({ netvisor: true })}>
															{t('NetvisorImportExport', 'Netvisor tuonti/vienti')}
														</NavDropdown.Item>
													)}

													{activeIntegrations.tokenProcountor && (
														<NavDropdown.Item onClick={() => setShowModal({ procountor: true })}>
															{t('ProcountorImportExport', 'Procountor tuonti/vienti')}
														</NavDropdown.Item>
													)}
													{activeIntegrations.tokenProcountor && (
														<NavDropdown.Item onClick={() => setShowModal({ procountorInvalidation: true })}>
															Procountor {t('SalaryDataDeviations', 'Palkkatietopoikkeamat')}
														</NavDropdown.Item>
													)}

													{!activeIntegrations.tokenProcountor && (
														<NavDropdown.Item onClick={() => navigate('/check-employees')}>
															{t('SalaryDataDeviations', 'Palkkatietopoikkeamat')}
														</NavDropdown.Item>
													)}
												</NavDropdown>
												{isBasicLevel && (
													<Nav.Link
														className="flex-fill text-end me-3"
														active={false}
														href="https://www.heeros.com/ratkaisut/palkka-ja-henkilostohallinto/hr-jarjestelma"
														target="_blank"
													>
														Basic HR
													</Nav.Link>
												)}
											</div>
										)}

									<NavDropdown.Divider />
									<NavDropdown.Item className="language-selector">
										<LanguageSelector />
									</NavDropdown.Item>
									<NavDropdown.Divider />

									<NavDropdown.Item onClick={() => setShowModal({ mfa: true })}>
										{t('SettingsLogin', 'Kirjautumisasetukset')}
									</NavDropdown.Item>

									<ChangePassword />
									<NavDropdown.Item onClick={logout}>{t('Logout', 'Kirjaudu ulos')}</NavDropdown.Item>
								</NavDropdown>
							</div>
						</Col>
					</Navbar.Collapse>
				</Container>
			</TopMenuContainer>
		</>
	)
}
